import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { useToast } from '@WESCO-International/wdp-ui-components/components/toast';
import { useLoadingOverlay } from './useLoadingOverlay';
import {
  cleanLinks,
  getSystemOptionsForDomain,
  parseTags,
  validateLinks,
} from '../components/common/utils';
import {
  ActiveTab,
  type FormState,
  type KindType,
  type SubmitPayload,
} from '../components/common/types';

const INITIAL_FORM: FormState = {
  name: '',
  namespace: 'default',
  owner: 'guest',
  description: '',
  system: '',
  domain: '',
  lifecycle: 'Proposed',
  tags: '',
  type: '',
  providesApis: [],
  consumesApis: [],
  subcomponentOf: '',
  apiType: '',
  apiVersion: '1.0.0',
  links: [{ url: '', title: '', icon: '' }],
};

const waitForPaint = () =>
  new Promise<void>(resolve => {
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      window.requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

const sleep = (ms: number) =>
  new Promise<void>(resolve => window.setTimeout(resolve, ms));

export const useAddEntityForm = () => {
  const [kind, setKind] = useState<KindType>('');
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [consumesSearch, setConsumesSearch] = useState('');
  const [providesSearch, setProvidesSearch] = useState('');

  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const navigate = useNavigate();
  const toast = useToast();

  const {
    open: isSaving,
    show: showSaving,
    hide: hideSaving,
    overlay,
  } = useLoadingOverlay();

  // ── field helpers ──────────────────────────────────────────────
  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm(f => ({ ...f, [field]: value }));

  const setName = (raw: string) => setField('name', raw.replace(/\s+/g, '-'));

  // ── system options driven by domain ───────────────────────────
  const systemOptions = useMemo(() => {
    const opts = getSystemOptionsForDomain(form.domain);

    if (opts.length === 0) {
      if (form.system) setForm(f => ({ ...f, system: '' }));
      return opts;
    }

    const systemExists = opts.some(o => o.value === form.system);
    if (!systemExists) setForm(f => ({ ...f, system: opts[0].value }));

    return opts;
  }, [form.domain, form.system]);

  // ── link helpers ───────────────────────────────────────────────
  const MAX_LINKS = 10;

  const addLink = () => {
    if (form.links.length >= MAX_LINKS) return;
    setField('links', [...form.links, { url: '', title: '' }]);
  };

  const removeLink = (index: number) =>
    setField(
      'links',
      form.links.filter((_, i) => i !== index),
    );

  const updateLink = (
    index: number,
    patch: Partial<FormState['links'][number]>,
  ) => {
    const next = [...form.links];
    next[index] = { ...next[index], ...patch };
    setField('links', next);
  };

  // ── domain change ──────────────────────────────────────────────
  const changeDomain = (nextDomain: string) => {
    setForm(f => {
      const nextSystems = getSystemOptionsForDomain(nextDomain);
      const nextSystem = nextSystems.some(o => o.value === f.system)
        ? f.system
        : nextSystems[0]?.value ?? '';
      return { ...f, domain: nextDomain, system: nextSystem };
    });
  };

  // ── kind change ────────────────────────────────────────────────
  const changeKind = (next: KindType) => {
    setKind(next);
    if (next === 'Component') {
      setForm(f => ({ ...f, type: f.type || '' }));
    }
  };

  // ── validation ─────────────────────────────────────────────────
  const isApiKind = kind === 'API';
  const isComponentKind = kind === 'Component';
  const isMissingRequired =
    !kind ||
    !form.name.trim() ||
    !form.owner.trim() ||
    !form.system.trim() ||
    !form.domain.trim() ||
    (isApiKind && !form.apiType.trim()) ||
    (isComponentKind && !form.type.trim());

  // ── payload builder ────────────────────────────────────────────
  const buildPayload = (): SubmitPayload => {
    const tags = parseTags(form.tags);
    const links = cleanLinks(form.links);
    const lifecycle = 'Proposed';

    if (kind === 'API') {
      const apiType = form.apiType.trim();
      return {
        entityKind: 'API',
        name: form.name,
        namespace: 'default',
        owner: form.owner,
        description: form.description,
        lifecycle,
        system: form.system,
        domain: form.domain,
        type: apiType,
        tags,
        links,
        apiVersion: form.apiVersion || '1.0.0',
      };
    }

    return {
      entityKind: 'Component',
      name: form.name,
      namespace: 'default',
      owner: form.owner,
      description: form.description,
      type: form.type.trim(),
      lifecycle,
      system: form.system,
      domain: form.domain,
      tags,
      links,
      consumesApis: form.consumesApis,
      providesApis: form.providesApis,
      ...(form.subcomponentOf ? { subcomponentOf: form.subcomponentOf } : {}),
    };
  };

  // ── submit ─────────────────────────────────────────────────────
  const propose = async () => {
    if (isSaving) return;

    const rawTags = form.tags
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);

    const badTag = rawTags.find(t => {
      const norm = t
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9+#-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 63);
      return norm === '';
    });

    if (badTag) {
      toast.errorMsg(
        `Tag "${badTag}" is invalid. Use lowercase letters, numbers, '+', '#', and '-' (max 63 chars).`,
      );
      return;
    }

    const linkCheck = validateLinks(form.links);
    if (!linkCheck.valid) {
      toast.errorMsg(linkCheck.message ?? 'Please fix link details.');
      return;
    }

    const missing: string[] = [];
    if (!kind) missing.push('Kind');
    if (!form.name.trim()) missing.push('Name');
    if (!form.owner.trim()) missing.push('Owner');
    if (!form.system.trim()) missing.push('System');
    if (!form.domain.trim()) missing.push('Domain');
    if (isApiKind && !form.apiType.trim()) missing.push('API Type');
    if (isComponentKind && !form.type.trim()) missing.push('Component Type');

    if (missing.length) {
      toast.errorMsg(`Please fill required fields: ${missing.join(', ')}`);
      return;
    }

    showSaving();
    await waitForPaint();

    try {
      const { token } = await identityApi.getCredentials();
      const backendBaseUrl = configApi.getString('backend.baseUrl');

      // Client-side dry-run validation before submit
      const dryRunRes = await fetch(
        `${backendBaseUrl}/api/catalog-portal/dry-run`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify(buildPayload()),
        },
      );

      if (!dryRunRes.ok) {
        const stripNoise = (input: string) =>
          input
            .replace(/RequestId:[^\n\r]+/gi, '')
            .replace(/Time:[^\n\r]+/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

        let msg = 'Dry-run failed';

        try {
          const body = await dryRunRes.json();
          const code = body?.error?.code;
          const rawDetail = body?.error?.message || body?.error?.error?.message;
          const detail =
            typeof rawDetail === 'string' ? stripNoise(rawDetail) : rawDetail;
          msg = [code, detail].filter(Boolean).join(': ');
        } catch (err) {
          const fallback = (await dryRunRes.text().catch(() => '')) || msg;
          msg = stripNoise(fallback);
        }

        toast.error({ msg: msg || 'Dry-run failed', timer: 3000 });
        hideSaving();
        return;
      }

      const res = await fetch(`${backendBaseUrl}/api/catalog-portal/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify(buildPayload()),
      });

      if (!res.ok) {
        toast.errorMsg(
          'Something went wrong while submitting for approval. Please try again.',
        );
        hideSaving();
        return;
      }

      toast.successMsg('Submitted for approval.');
      await sleep(2000);
      hideSaving();
      navigate('/catalog', {
        state: { activeTab: ActiveTab.Proposals, refreshAfterCreate: true },
      });
    } catch (e) {
      console.error('Submit proposal error:', e);
      toast.errorMsg(
        'Something went wrong while submitting for approval. Please try again.',
      );
      hideSaving();
    }
  };

  return {
    kind,
    changeKind,
    form,
    setField,
    setName,
    changeDomain,
    systemOptions,
    addLink,
    removeLink,
    updateLink,
    consumesSearch,
    setConsumesSearch,
    providesSearch,
    setProvidesSearch,
    isMissingRequired,
    isSaving,
    overlay,
    propose,
    MAX_LINKS,
  };
};
